import React, { useCallback, useContext, useRef, useState } from "react"
import { ScreenSkeleton } from "@common/screens/ScreenSkeleton"
import {
    Dimensions,
    Image,
    Platform,
    ScrollView,
    StyleSheet,
    View,
} from "react-native"
import { useCheckout } from "@stripe/react-stripe-js/checkout"
import * as amplitude from "@amplitude/analytics-react-native"
import logo from "@assets/logo-blue.png"
import { NewSubscriptionAccompanimentCard } from "./components/NewSubscriptionAccompanimentCard"
import { NewSubscriptionPlanCard } from "./components/NewSubscriptionPlanCard"
import {
    COMMITMENT_GUARANTEES,
    NO_COMMITMENT_GUARANTEES,
    NewSubscriptionGuaranteesCard,
} from "./components/NewSubscriptionGuaranteesCard"
import { NewSubscriptionNextStepsCard } from "./components/NewSubscriptionNextStepsCard"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { NewSubscriptionPaymentCard } from "./components/NewSubscriptionPaymentCard"
import { NewSubscriptionAddressCard } from "./components/NewSubscriptionAddressCard"
import ErrorTextM from "../../common/components/text/ErrorTextM"
import { DiscountCard } from "../components/DiscountCard"
import { StageRecapCard } from "./components/StageRecapCard"
import { SavedCardChoiceCard } from "./components/SavedCardChoiceCard"
import GradientButtonM from "../../common/components/button/GradientButtonM"
import { FontAwesome5 } from "@expo/vector-icons"
import LinkTextM from "../../common/components/text/LinkTextM"
import TextM from "../../common/components/text/TextM"
import { useSubscriptionPricing } from "./hooks/useSubscriptionPricing"
import { usePaymentElementWatch } from "./hooks/usePaymentElementWatch"
import {
    HOSTED_FALLBACK_LABEL,
    PAYMENT_SLOW_MS,
    isPaymentElementFailed,
} from "./newSubscriptionUtils"
import { theme } from "@common/theme/theme"
import SubscriptionHelpButton from "../components/SubscriptionHelpFAB"

const Wrapper = ({ isLargeScreen, scrollViewRef, children }) => {
    return isLargeScreen ? (
        <ScrollView
            ref={scrollViewRef}
            contentContainerStyle={{ paddingHorizontal: 10, gap: 20 }}
            showsVerticalScrollIndicator={false}
        >
            {children}
        </ScrollView>
    ) : (
        <View style={{ flex: 1, gap: 20 }}>{children}</View>
    )
}

export const CreateNewSubscriptionScreenContent = ({
    plan,
    schoolSubjects,
    pricingTable,
    sessionsPerWeek,
    user,
    checkoutSession,
    openEditPlanModal,
    isStage,
    holidayCourses,
    subscriptionMetadata,
    hideAddressCard,
    savedCard,
    onReserveWithSavedCard,
    onReserved,
    // Qui monte ce formulaire, pour lire le parcours dans Amplitude.
    surface = "create_subscription",
    // Repli sur la page de Stripe ({@link useHostedCheckoutFallback}), fourni
    // par la page du lien de vente seulement.
    hostedFallback,
}) => {
    const dimensions = useContext(DimensionsContext)
    const isLargeScreen = dimensions?.width > 900

    const checkoutState = useCheckout()
    const [errorPayment, setErrorPayment] = useState(null)
    const [loadingPayment, setLoadingPayment] = useState(false)
    // Paiement en cours depuis plus de PAYMENT_SLOW_MS : le repli redevient proposé.
    const [paymentSlow, setPaymentSlow] = useState(false)
    // Carte déjà enregistrée : proposée par défaut, sans ressaisie.
    const [useSavedCard, setUseSavedCard] = useState(!!savedCard)
    const cardCompleteRef = useRef(false)
    const scrollViewRef = useRef(null)
    const paymentCardRef = useRef(null)
    const addressRef = useRef(null)
    const addressCardViewRef = useRef(null)

    const paymentElement = usePaymentElementWatch({
        surface,
        active: !useSavedCard,
    })
    const elementFailed = isPaymentElementFailed(paymentElement.status)
    // Ce que la page conseille quand le paiement ne passe pas.
    const unblockHint = hostedFallback
        ? "payez sur la page sécurisée Stripe (lien ci-dessous)"
        : "rechargez la page ou essayez un autre navigateur"

    const selectedSchoolSubjects = schoolSubjects?.reduce((acc, x) => {
        return plan?.subjectIds?.includes(x?.id)
            ? [...acc, { id: x.id, name: x.name, color: x.color, icon: x.icon }]
            : acc
    }, [])

    const {
        hoursPerMonth,
        monthlyPrice,
        hourlyPrice,
        noDiscountPrice,
        discountDeadline,
        discountAmount,
    } = useSubscriptionPricing({
        pricingTable: pricingTable,
        sessionsPerWeek: sessionsPerWeek,
        plan,
        user,
        amountToPay: checkoutSession?.amount,
        isDiscountApplied: checkoutSession?.is_discount_applied,
        subscriptionMetadata,
    })

    const scrollToRef = useCallback((ref) => {
        if (Platform.OS === "web") {
            ref.current?.scrollIntoView?.({
                behavior: "smooth",
                block: "start",
            })
        } else {
            ref.current?.measureLayout(
                scrollViewRef.current,
                (_x, y) => {
                    scrollViewRef.current?.scrollTo({ y, animated: true })
                },
                () => {}
            )
        }
    }, [])

    const handlePayment = useCallback(async () => {
        setLoadingPayment(true)
        setPaymentSlow(false)
        setErrorPayment(null)
        amplitude.track("checkout.pay_clicked", {
            surface,
            payment_element: useSavedCard
                ? "saved_card"
                : paymentElement.status,
        })

        const pay = async () => {
            // Formulaire absent : Stripe refuserait la confirmation (« Payment
            // Element non monté ») et la famille lirait une erreur inattendue.
            if (!useSavedCard && elementFailed) {
                setErrorPayment(
                    `Le formulaire de paiement ne s'affiche pas sur ce navigateur : ${unblockHint}.`
                )
                scrollToRef(paymentCardRef)
                return
            }

            // Validate address first
            let addressSaved = true
            if (addressRef.current?.save) {
                addressSaved = await addressRef.current.save()
            }

            if (useSavedCard && addressSaved) {
                const { error } = await onReserveWithSavedCard()
                if (error) {
                    setErrorPayment(error)
                } else {
                    onReserved?.()
                }
                return
            }

            // If address failed and card is also incomplete, scroll to card (higher priority)
            if (!addressSaved && !cardCompleteRef.current && !useSavedCard) {
                setLoadingPayment(false)
                scrollToRef(paymentCardRef)
                return
            }

            // If address failed but card looks complete, scroll to address
            if (!addressSaved) {
                setLoadingPayment(false)
                scrollToRef(addressCardViewRef)
                return
            }

            const { error } = await checkoutState.checkout.confirm()

            if (error) {
                amplitude.track("checkout.pay_error", {
                    surface,
                    message: error.message,
                })
                setErrorPayment(error.message)
                scrollToRef(paymentCardRef)
            }
        }

        // Un clic ne tourne plus sans issue : un script bloqué par le navigateur
        // ne rejette pas toujours sa promesse. Passé ce délai, la page propose le
        // repli — sans libérer le bouton, car une validation 3-D Secure dans
        // l'appli bancaire dépasse souvent ce délai, et un second clic lancerait
        // une confirmation concurrente.
        const slowTimer = setTimeout(() => {
            setPaymentSlow(true)
            amplitude.track("checkout.pay_timeout", { surface })
            setErrorPayment(
                `Le paiement met du temps à répondre. Si vous validez dans votre appli bancaire, continuez ; sinon, ${unblockHint}.`
            )
        }, PAYMENT_SLOW_MS)

        try {
            await pay()
        } catch (err) {
            console.error(err)
            setErrorPayment("Une erreur inattendue s'est produite.")
            scrollToRef(paymentCardRef)
        } finally {
            clearTimeout(slowTimer)
            setPaymentSlow(false)
            setLoadingPayment(false)
        }
    }, [
        checkoutState,
        scrollToRef,
        useSavedCard,
        onReserveWithSavedCard,
        onReserved,
        surface,
        paymentElement.status,
        elementFailed,
        unblockHint,
    ])

    return (
        <ScreenSkeleton
            withCenterContent
            withScroll={!isLargeScreen}
            scrollViewRef={!isLargeScreen ? scrollViewRef : undefined}
            contentContainerStyle={{ paddingHorizontal: 10 }}
            contentWrapperStyle={{ paddingHorizontal: 0, gap: 0 }}
            bottomContent={
                <View
                    style={{
                        width: Dimensions.get("window").width,
                        ...styles.bottomContent,
                    }}
                >
                    {(errorPayment || hostedFallback?.error) && (
                        <ErrorTextM
                            errorData={errorPayment ?? hostedFallback.error}
                        />
                    )}

                    <GradientButtonM
                        loading={loadingPayment}
                        label={
                            checkoutSession?.amount > 0
                                ? `Confirmer et payer ${checkoutSession?.amount}€`
                                : "Confirmer l'inscription"
                        }
                        icon={
                            <FontAwesome5 name="lock" size={18} color="white" />
                        }
                        onPress={handlePayment}
                    />

                    <TextM center wrap>
                        En cliquant, j'accepte les{" "}
                        <LinkTextM url="https://masteur.com/cgu">
                            Conditions générales d'utilisation
                        </LinkTextM>
                    </TextM>

                    {/* Masqué pendant une confirmation en cours (3-D Secure) : une
                        session Stripe ouverte à ce moment ferait payer deux fois.
                        Il revient avec le message du paiement lent. */}
                    {hostedFallback && (!loadingPayment || paymentSlow) && (
                        <TextM
                            center
                            wrap
                            fontSize={12}
                            color={theme.colors.grey}
                        >
                            Un problème pour payer ?{" "}
                            <LinkTextM
                                onPress={() =>
                                    hostedFallback.openHostedCheckout("manual")
                                }
                            >
                                {HOSTED_FALLBACK_LABEL}
                            </LinkTextM>
                        </TextM>
                    )}
                </View>
            }
        >
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    paddingTop: 20,
                    paddingBottom: 10,
                }}
            >
                <View style={{ position: "absolute", right: 10 }}>
                    <SubscriptionHelpButton />
                </View>
                <Image
                    source={logo}
                    style={{ width: 150, height: 50 }}
                    resizeMode="contain"
                />
            </View>
            <View
                style={{
                    flexDirection: isLargeScreen ? "row-reverse" : "column",
                    gap: 20,
                    flex: 1,
                }}
            >
                <View
                    style={{
                        minWidth: isLargeScreen ? 380 : 0,
                        maxWidth: isLargeScreen ? dimensions.width / 3 : "100%",
                    }}
                >
                    {isStage ? (
                        <StageRecapCard
                            schoolSubjects={schoolSubjects}
                            holidayCourses={holidayCourses}
                            amountToPay={checkoutSession?.amount}
                            subscriptionMetadata={subscriptionMetadata}
                        />
                    ) : (
                        <>
                            <DiscountCard
                                discountDeadline={discountDeadline}
                                discountAmount={discountAmount}
                            />
                            <NewSubscriptionAccompanimentCard
                                selectedSchoolSubjects={selectedSchoolSubjects}
                                plan={plan}
                                amountToPay={checkoutSession?.amount}
                                hoursPerMonth={hoursPerMonth}
                                monthlyPrice={monthlyPrice}
                                hourlyPrice={hourlyPrice}
                                noDiscountPrice={noDiscountPrice}
                                discountAmount={discountAmount}
                                firstMonth={checkoutSession?.first_month}
                                schedule={checkoutSession?.schedule}
                                paymentCollectionDay={
                                    checkoutSession?.schedule?.collection_day ??
                                    user?.payment_collection_day
                                }
                            />
                        </>
                    )}
                </View>

                <Wrapper
                    isLargeScreen={isLargeScreen}
                    scrollViewRef={isLargeScreen ? scrollViewRef : undefined}
                >
                    {!isStage && (
                        <NewSubscriptionPlanCard
                            plan={plan}
                            openEditPlanModal={openEditPlanModal}
                            subjects={selectedSchoolSubjects}
                        />
                    )}
                    {!isStage && (
                        <NewSubscriptionGuaranteesCard
                            items={
                                plan?.withCommitment
                                    ? COMMITMENT_GUARANTEES
                                    : NO_COMMITMENT_GUARANTEES
                            }
                        />
                    )}
                    {!!savedCard && (
                        <SavedCardChoiceCard
                            savedCard={savedCard}
                            useSavedCard={useSavedCard}
                            onChange={(value) => {
                                setErrorPayment(null)
                                setUseSavedCard(value)
                            }}
                        />
                    )}
                    {!useSavedCard && (
                        <NewSubscriptionPaymentCard
                            ref={paymentCardRef}
                            setError={setErrorPayment}
                            cardCompleteRef={cardCompleteRef}
                            onReady={paymentElement.onReady}
                            onLoadError={paymentElement.onLoadError}
                            elementFailed={elementFailed}
                            hostedFallback={hostedFallback}
                        />
                    )}
                    {!hideAddressCard && (
                        <View ref={addressCardViewRef}>
                            <NewSubscriptionAddressCard
                                ref={addressRef}
                                userEmail={user?.email}
                                user={user}
                                address={user?.address}
                            />
                        </View>
                    )}
                    {!isStage && (
                        <NewSubscriptionNextStepsCard
                            studentFirstname={user?.firstname}
                        />
                    )}
                </Wrapper>
            </View>
        </ScreenSkeleton>
    )
}

const styles = StyleSheet.create({
    bottomContent: {
        alignSelf: "center",
        gap: 10,
        padding: 5,
        paddingBottom: 10,
        paddingTop: 10,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
        alignItems: "center",
    },
    logoContainer: {
        alignItems: "center",
        paddingTop: 20,
        paddingBottom: 10,
    },
})
